export const ACCESSIBILITY_UI_SETTINGS_KEY = 'accessibility_preferences';
export const LEGACY_ACCESSIBILITY_STORAGE_KEY = 'lumeratec-a11y-prefs';

const TEXT_SCALE_CLASSES = Array.from(
  { length: 5 },
  (_, index) => `a11y-text-scale-${index + 1}`
);

const COLOR_VISION_CLASSES = [
  'a11y-color-vision-deuteranopia',
  'a11y-color-vision-protanopia',
  'a11y-color-vision-tritanopia',
];

const matchesMediaQuery = query =>
  typeof window.matchMedia === 'function' && window.matchMedia(query).matches;

export const createDefaultAccessibilityPreferences = () => ({
  profile: 'default',
  highContrast: false,
  darkMode: matchesMediaQuery('(prefers-color-scheme: dark)'),
  colorVision: 'default',
  textScale: 0,
  readingFont: false,
  highlightLinks: false,
  pauseAnimations: matchesMediaQuery('(prefers-reduced-motion: reduce)'),
  enhancedFocus: true,
  keyboardNavigation: false,
  screenReaderMode: false,
  conversationBriefing: false,
  announceSla: false,
  announcementVerbosity: 'essential',
});

export const normalizeAccessibilityPreferences = preferences => {
  const defaults = createDefaultAccessibilityPreferences();
  const normalized = { ...defaults, ...(preferences || {}) };

  if (preferences?.dyslexiaFont !== undefined) {
    normalized.readingFont = preferences.dyslexiaFont;
  }

  if (preferences?.colorBlind && !preferences.colorVision) {
    normalized.colorVision = 'deuteranopia';
  }

  normalized.textScale = Math.min(
    5,
    Math.max(0, Number(normalized.textScale) || 0)
  );

  if (
    !['default', 'deuteranopia', 'protanopia', 'tritanopia'].includes(
      normalized.colorVision
    )
  ) {
    normalized.colorVision = 'default';
  }

  if (!['essential', 'detailed'].includes(normalized.announcementVerbosity)) {
    normalized.announcementVerbosity = 'essential';
  }

  return Object.fromEntries(
    Object.keys(defaults).map(key => [key, normalized[key]])
  );
};

export const preferencesForProfile = (profile, currentPreferences = {}) => {
  const defaults = createDefaultAccessibilityPreferences();
  const shared = {
    ...defaults,
    darkMode: currentPreferences.darkMode ?? defaults.darkMode,
    colorVision: currentPreferences.colorVision || defaults.colorVision,
  };

  const profiles = {
    default: {
      ...defaults,
      darkMode: currentPreferences.darkMode ?? defaults.darkMode,
    },
    lowVision: {
      ...shared,
      highContrast: true,
      textScale: 2,
      highlightLinks: true,
      enhancedFocus: true,
    },
    nonVisual: {
      ...shared,
      pauseAnimations: true,
      enhancedFocus: true,
      keyboardNavigation: true,
      screenReaderMode: true,
      conversationBriefing: true,
      announceSla: true,
      announcementVerbosity: 'detailed',
    },
    keyboard: {
      ...shared,
      pauseAnimations: true,
      enhancedFocus: true,
      keyboardNavigation: true,
    },
  };

  return { ...(profiles[profile] || shared), profile };
};

export const applyAccessibilityPreferences = preferences => {
  const options = normalizeAccessibilityPreferences(preferences);
  const body = document.body;
  const html = document.documentElement;

  body.classList.toggle('a11y-high-contrast', options.highContrast);
  body.classList.toggle('a11y-reading-font', options.readingFont);
  body.classList.toggle('a11y-highlight-links', options.highlightLinks);
  body.classList.toggle('a11y-pause-animations', options.pauseAnimations);
  body.classList.toggle('a11y-enhanced-focus', options.enhancedFocus);
  body.classList.toggle('a11y-keyboard-navigation', options.keyboardNavigation);
  body.classList.toggle('a11y-screen-reader-mode', options.screenReaderMode);

  COLOR_VISION_CLASSES.forEach(className => body.classList.remove(className));
  if (options.colorVision !== 'default') {
    body.classList.add(`a11y-color-vision-${options.colorVision}`);
  }

  html.classList.remove(...TEXT_SCALE_CLASSES);
  if (options.textScale > 0) {
    html.classList.add(`a11y-text-scale-${options.textScale}`);
  }

  window.dispatchEvent(
    new CustomEvent('a11y-preferences-changed', { detail: options })
  );
};

export const announceAccessibilityMessage = (message, priority = 'polite') => {
  window.dispatchEvent(
    new CustomEvent('a11y-announce', {
      detail: { message, priority },
    })
  );
};
